import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, Button, Progress, Alert, message, Checkbox, Upload } from "antd";
import Dragger from "antd/es/upload/Dragger";
import { InboxOutlined } from "@ant-design/icons";
import { useForm } from "antd/es/form/Form";
import { FaCloudUploadAlt } from "react-icons/fa";
import { Content } from "antd/es/layout/layout";
import { request, doRequest, token } from "../../../functions/request";

// Referência usada pelo player/app para achar o backup quando a Bunny falha.
// Sem acentos/espaços porque ela vai na URL de /video_backup/resolve/:ref.
function normalizarReferencia(texto) {
  return String(texto || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

const LIMITE_BYTES = 6 * 1024 ** 3; // 6 GB

export default function ModalBackupUpload({ open, onCancel, onFinish }) {
  const [form] = useForm();
  const [fileList, setFileList] = useState([]);
  const [videos, setVideos] = useState([]);
  const [progresso, setProgresso] = useState(0);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setFileList([]);
    setProgresso(0);
    setEnviando(false);
    setErro(null);

    request("/video_analise/getVideosAnalise", { method: "GET" })
      .then((resposta) => setVideos(Array.isArray(resposta) ? resposta : []))
      .catch((error) => {
        console.log(error);
        setVideos([]);
      });
  }, [open, form]);

  // Ao escolher o vídeo relacionado já preenchemos título, guid da Bunny e a
  // referência de fallback — é o caminho normal de uso da tela.
  const handleSelecionarVideo = (vidId) => {
    const video = videos.find((v) => v.vid_id === vidId);
    if (!video) return;

    form.setFieldsValue({
      titulo: form.getFieldValue("titulo") || video.vid_titulo,
      vid_cdn: video.vid_cdn || "",
      vid_end: normalizarReferencia(video.vid_end || video.vid_titulo),
    });
  };

  const enviar = (values) => {
    const arquivo = fileList[0];
    if (!arquivo) {
      setErro("Selecione o arquivo de vídeo.");
      return;
    }

    const referencia = normalizarReferencia(values.vid_end || values.titulo);

    const campos = new FormData();
    campos.append("file", arquivo);
    campos.append("titulo", values.titulo);
    campos.append("vid_end", referencia);
    if (values.vid_id) campos.append("vid_id", values.vid_id);
    if (values.vid_cdn) campos.append("vid_cdn", values.vid_cdn);
    if (values.observacao) campos.append("observacao", values.observacao);

    setEnviando(true);
    setErro(null);
    setProgresso(0);

    // XHR em vez de fetch porque só ele expõe progresso de upload — o arquivo
    // costuma ter centenas de MB e a barra é a única referência do usuário.
    const xhr = new XMLHttpRequest();
    xhr.open("POST", process.env.REACT_APP_API_RB + "/video_backup/upload");
    xhr.setRequestHeader("x-access-token", token);

    xhr.upload.onprogress = (evento) => {
      if (!evento.lengthComputable) return;
      setProgresso(Math.round((evento.loaded / evento.total) * 100));
    };

    xhr.onload = async () => {
      setEnviando(false);

      if (xhr.status < 200 || xhr.status >= 300) {
        let detalhe = "Erro ao enviar o vídeo.";
        try {
          detalhe = JSON.parse(xhr.responseText).error || detalhe;
        } catch (_) {
          // O 413 que não vem em JSON é do nginx, na frente da API: o arquivo
          // passou do limite do servidor web e nem chegou a ser recebido.
          if (xhr.status === 413) {
            detalhe =
              "O servidor recusou o arquivo por ser grande demais. Avise o suporte técnico " +
              "informando o tamanho do vídeo.";
          }
        }
        setErro(detalhe);
        return;
      }

      // Grava a referência no vid_end do vídeo relacionado. Opcional porque
      // sobrescreve o valor atual do campo (endereço antigo do Vimeo).
      if (values.vid_id && values.atualizar_vid_end) {
        const campos_video = new FormData();
        campos_video.append("newData", JSON.stringify({ vid_end: referencia }));
        await doRequest(`/video_analise/updateVideoAnalise/${values.vid_id}`, {
          method: "PUT",
          body: campos_video,
        }).catch((error) => {
          console.log(error);
          message.warning("Backup enviado, mas não foi possível atualizar o vid_end do vídeo.");
        });
      }

      message.success("Vídeo enviado! A transcodificação começou e você acompanha na lista.");
      onFinish();
      onCancel();
    };

    xhr.onerror = () => {
      setEnviando(false);
      setErro("Falha de conexão durante o upload.");
    };

    xhr.send(campos);
  };

  const uploadProps = {
    onRemove: () => setFileList([]),
    beforeUpload: (file) => {
      // Mesmo limite do multer na API (route_video_backup.js). Barrar aqui
      // poupa o usuário de esperar gigabytes subirem só para a API recusar.
      if (file.size > LIMITE_BYTES) {
        setErro(
          `O arquivo tem ${(file.size / 1024 ** 3).toFixed(1).replace(".", ",")} GB; o limite é de 6 GB.`
        );
        return Upload.LIST_IGNORE;
      }
      setErro(null);
      setFileList([file]);
      if (!form.getFieldValue("titulo")) {
        form.setFieldsValue({ titulo: file.name.replace(/\.[^.]+$/, "") });
      }
      return false;
    },
    fileList,
    maxCount: 1,
    accept: "video/*,.mkv,.mov,.mp4,.avi,.webm",
  };

  return (
    <Modal
      title="Enviar vídeo para backup"
      open={open}
      onCancel={enviando ? undefined : onCancel}
      okButtonProps={{ style: { display: "none" } }}
      cancelButtonProps={{ style: { display: "none" } }}
      maskClosable={!enviando}
      closable={!enviando}
      width={720}
    >
      {open && (
        <Form form={form} layout="vertical" variant="filled" onFinish={enviar}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="O arquivo é transcodificado para HLS (360p, 720p e 1080p) e fica no servidor da Rural Business, servindo de fallback quando a CDN da Bunny falha."
          />

          <Form.Item
            name="arquivo"
            rules={[{ required: fileList.length === 0, message: "Selecione o arquivo de vídeo!" }]}
          >
            <Dragger {...uploadProps} disabled={enviando}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Arraste o arquivo de vídeo ou clique para selecionar</p>
              <p className="ant-upload-hint">MP4, MOV, MKV, AVI ou WEBM — até 6 GB</p>
            </Dragger>
          </Form.Item>

          <Form.Item
            label="Título"
            name="titulo"
            rules={[{ required: true, message: "Preencha o título!" }]}
          >
            <Input disabled={enviando} />
          </Form.Item>

          <Content style={{ display: "flex", gap: 12 }}>
            <Form.Item label="Vídeo relacionado" name="vid_id" style={{ flex: 1 }}>
              <Select
                allowClear
                showSearch
                disabled={enviando}
                placeholder="Selecione o vídeo do acervo (opcional)"
                optionFilterProp="label"
                onChange={handleSelecionarVideo}
                options={videos.map((v) => ({ value: v.vid_id, label: v.vid_titulo }))}
              />
            </Form.Item>
            <Form.Item label="Endereço CDN (vid_cdn)" name="vid_cdn" style={{ flex: 1 }}>
              <Input disabled={enviando} placeholder="guid da Bunny" />
            </Form.Item>
          </Content>

          <Form.Item
            label="Referência de fallback (vid_end)"
            name="vid_end"
            extra="É por esse valor que o player e o app encontram o backup quando o vid_cdn da Bunny falha. Se ficar vazio, usamos o título."
          >
            <Input disabled={enviando} placeholder="ex: analise-soja-2026-01" />
          </Form.Item>

          <Form.Item name="atualizar_vid_end" valuePropName="checked">
            <Checkbox disabled={enviando}>
              Gravar essa referência no campo vid_end do vídeo relacionado (substitui o valor atual)
            </Checkbox>
          </Form.Item>

          <Form.Item label="Observação" name="observacao">
            <Input.TextArea rows={2} disabled={enviando} />
          </Form.Item>

          {enviando && (
            <Progress
              percent={progresso}
              status="active"
              style={{ marginBottom: 12 }}
              format={(p) => (p === 100 ? "Processando no servidor..." : `${p}%`)}
            />
          )}

          {erro && <Alert type="error" showIcon message={erro} style={{ marginBottom: 12 }} />}

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              icon={<FaCloudUploadAlt />}
              loading={enviando}
              size="large"
            >
              {enviando ? "Enviando..." : "Enviar e transcodificar"}
            </Button>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}
